import type {
  TimerState,
  TimerConfig,
  EngineEvent,
  EventListener,
  EventUnsubscribe,
} from './types';
import { EventEmitter, createEvent } from './events';

/**
 * Get default timer state
 */
export function getDefaultTimerState(duration: number): TimerState {
  return {
    duration,
    remaining: duration,
    elapsed: 0,
    isRunning: false,
    isPaused: false,
    isExpired: false,
    startTime: null,
    pausedAt: null,
  };
}

/**
 * TimerController - Manages countdown timer
 */
export class TimerController {
  private contentId: string;
  private state: TimerState;
  private config: Required<Omit<TimerConfig, 'onTick' | 'onWarning' | 'onExpire'>> & 
    Pick<TimerConfig, 'onTick' | 'onWarning' | 'onExpire'>;
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private emitter: EventEmitter = new EventEmitter();
  private warningEmitted: boolean = false;

  constructor(contentId: string, config: TimerConfig) {
    this.contentId = contentId;
    this.config = {
      warningThreshold: 30,
      autoSubmitOnExpire: false,
      ...config,
    };

    this.state = getDefaultTimerState(config.duration);
  }

  /**
   * Get current timer state
   */
  getState(): TimerState {
    return { ...this.state };
  }

  /**
   * Get content ID
   */
  getContentId(): string {
    return this.contentId;
  }

  /**
   * Get remaining time in seconds
   */
  getRemaining(): number {
    return this.state.remaining;
  }

  /**
   * Get elapsed time in seconds
   */
  getElapsed(): number {
    return this.state.elapsed;
  }

  /**
   * Get progress percentage (0-100)
   */
  getProgress(): number {
    if (this.state.duration === 0) return 100;
    return ((this.state.duration - this.state.remaining) / this.state.duration) * 100;
  }

  /**
   * Get formatted time string (MM:SS)
   */
  getFormattedTime(seconds?: number): string {
    const time = seconds ?? this.state.remaining;
    const mins = Math.floor(time / 60);
    const secs = time % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }

  /**
   * Get formatted time with hours (HH:MM:SS)
   */
  getFormattedTimeLong(seconds?: number): string {
    const time = seconds ?? this.state.remaining;
    const hours = Math.floor(time / 3600);
    const mins = Math.floor((time % 3600) / 60);
    const secs = time % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }

  /**
   * Check if timer is in warning state
   */
  isWarning(): boolean {
    return (
      this.state.isRunning &&
      this.state.remaining <= (this.config.warningThreshold ?? 30)
    );
  }

  /**
   * Start the timer
   */
  start(): void {
    if (this.state.isRunning || this.state.isExpired) return;

    this.state.isRunning = true;
    this.state.isPaused = false;
    this.state.startTime = Date.now();

    this.emit(createEvent('timer:start', this.contentId, {
      duration: this.state.duration,
      remaining: this.state.remaining,
    }));

    this.intervalId = setInterval(() => this.tick(), 1000);
  }

  /**
   * Pause the timer
   */
  pause(): void {
    if (!this.state.isRunning || this.state.isPaused) return;

    this.state.isPaused = true;
    this.state.pausedAt = Date.now();

    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }

    this.emit(createEvent('timer:pause', this.contentId, {
      remaining: this.state.remaining,
      elapsed: this.state.elapsed,
    }));
  }

  /**
   * Resume the timer
   */
  resume(): void {
    if (!this.state.isPaused) return;

    this.state.isPaused = false;
    this.state.pausedAt = null;

    this.emit(createEvent('timer:resume', this.contentId, {
      remaining: this.state.remaining,
    }));

    this.intervalId = setInterval(() => this.tick(), 1000);
  }

  /**
   * Stop the timer
   */
  stop(): void {
    this.state.isRunning = false;
    this.state.isPaused = false;

    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  /**
   * Reset the timer
   */
  reset(): void {
    this.stop();
    this.state = getDefaultTimerState(this.config.duration);
    this.warningEmitted = false;

    this.emit(createEvent('timer:reset', this.contentId, {
      duration: this.state.duration,
    }));
  }

  /**
   * Set new duration
   */
  setDuration(duration: number): void {
    this.config.duration = duration;
    if (!this.state.isRunning) {
      this.state.duration = duration;
      this.state.remaining = duration;
    }
  }

  /**
   * Add time to the timer
   */
  addTime(seconds: number): void {
    this.state.remaining = Math.max(0, this.state.remaining + seconds);
    if (this.state.remaining > 0 && this.state.isExpired) {
      this.state.isExpired = false;
    }
  }

  /**
   * Subtract time from the timer
   */
  subtractTime(seconds: number): void {
    this.state.remaining = Math.max(0, this.state.remaining - seconds);
    if (this.state.remaining <= 0) {
      this.expire();
    }
  }

  /**
   * Handle tick
   */
  private tick(): void {
    if (this.state.remaining <= 0) {
      this.expire();
      return;
    }

    this.state.remaining -= 1;
    this.state.elapsed += 1;

    // Emit tick event
    this.emit(createEvent('timer:tick', this.contentId, {
      remaining: this.state.remaining,
      elapsed: this.state.elapsed,
    }));

    // Call tick callback
    this.config.onTick?.(this.state);

    // Check warning threshold
    if (
      !this.warningEmitted &&
      this.config.warningThreshold &&
      this.state.remaining <= this.config.warningThreshold
    ) {
      this.warningEmitted = true;
      
      this.emit(createEvent('timer:warning', this.contentId, {
        remaining: this.state.remaining,
      }));
      
      this.config.onWarning?.(this.state.remaining);
    }

    // Check expiration
    if (this.state.remaining <= 0) {
      this.expire();
    }
  }

  /**
   * Handle expiration
   */
  private expire(): void {
    this.stop();
    this.state.isExpired = true;
    this.state.remaining = 0;

    this.emit(createEvent('timer:expire', this.contentId, {
      elapsed: this.state.elapsed,
      autoSubmit: this.config.autoSubmitOnExpire,
    }));

    this.config.onExpire?.();
  }

  /**
   * Subscribe to timer events
   */
  on<T = unknown>(
    type: 'timer:start' | 'timer:tick' | 'timer:warning' | 'timer:pause' | 'timer:resume' | 'timer:expire' | 'timer:reset',
    listener: EventListener<T>
  ): EventUnsubscribe {
    return this.emitter.on(type, listener);
  }

  /**
   * Subscribe to all events
   */
  onAny<T = unknown>(listener: EventListener<T>): EventUnsubscribe {
    return this.emitter.onAny(listener);
  }

  /**
   * Emit event
   */
  private emit<T = unknown>(event: EngineEvent<T>): void {
    this.emitter.emit(event);
  }

  /**
   * Cleanup
   */
  destroy(): void {
    this.stop();
    this.emitter.removeAllListeners();
  }
}

/**
 * Create a timer controller
 */
export function createTimer(contentId: string, config: TimerConfig): TimerController {
  return new TimerController(contentId, config);
}

/**
 * Format seconds to MM:SS
 */
export function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Format seconds to HH:MM:SS
 */
export function formatTimeLong(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}
